import socialApiClient from "../helpers/services/social-api-client";

export type SocialAutoPostProjectSummary = {
  id: string;
  name: string;
  hasGeneratedContent?: boolean;
  createdAt?: string | number;
  updatedAt?: string | number;
};

export type SocialAutoPostProjectDetail = SocialAutoPostProjectSummary & {
  companyId?: number;
  userId?: number | null;
  draft?: Record<string, unknown> | null;
};

const client = () => socialApiClient();

const withCompany = (companyId: string | number | null | undefined) => ({
  params: { companyId },
});

function dataUrlToFile(dataUrl: string, name = "asset"): File {
  const [header, b64] = dataUrl.split(",");
  const mime = /data:([^;]+)/.exec(header)?.[1] || "image/png";
  const binary = atob(b64 || "");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const ext = mime.includes("png")
    ? "png"
    : mime.includes("webp")
    ? "webp"
    : "jpg";
  return new File([bytes], `${name}.${ext}`, { type: mime });
}

export const socialAutoPostsApi = {
  list: async (companyId: string | number) => {
    const { data } = await client().get<{
      payload: SocialAutoPostProjectSummary[];
    }>("/openai/auto-posts", withCompany(companyId));
    return Array.isArray(data.payload) ? data.payload : [];
  },

  get: async (companyId: string | number, id: string) => {
    const { data } = await client().get<{
      payload: SocialAutoPostProjectDetail;
    }>(`/openai/auto-posts/${encodeURIComponent(id)}`, withCompany(companyId));
    return data.payload;
  },

  upsert: async (
    companyId: string | number,
    body: {
      id: string;
      name?: string;
      hasGeneratedContent?: boolean;
      draft?: Record<string, unknown> | null;
    }
  ) => {
    const { data } = await client().put<{
      payload: SocialAutoPostProjectDetail;
    }>(
      `/openai/auto-posts/${encodeURIComponent(body.id)}`,
      body,
      withCompany(companyId)
    );
    return data.payload;
  },

  delete: async (companyId: string | number, id: string) => {
    const { data } = await client().delete<{
      payload: { id: string; deleted: boolean };
    }>(`/openai/auto-posts/${encodeURIComponent(id)}`, withCompany(companyId));
    return data.payload;
  },

  uploadFile: async (
    companyId: string | number,
    file: Blob,
    filename = "asset.png"
  ) => {
    const form = new FormData();
    form.append("images", file, filename);
    const { data } = await client().post<{ payload: { url: string } }>(
      "/openai/auto-posts/upload",
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
        ...withCompany(companyId),
      }
    );
    return data.payload?.url || null;
  },

  uploadDataUrl: async (
    companyId: string | number,
    dataUrl: string,
    name = "asset"
  ) => {
    if (!dataUrl?.startsWith("data:")) return dataUrl;
    const file = dataUrlToFile(dataUrl, name);
    if (!file.size) return null;
    return socialAutoPostsApi.uploadFile(companyId, file, file.name);
  },
};
